'use client';

import { CmsEditorialIcon } from '@/cms/components/blocks/shared/cms-editorial-icon';
import { CmsNavLinkBadge } from '@/cms/components/chrome/cms-nav-link-badge';
import {
  type CmsSiteChromeEditTargetAttributes,
  type CmsSiteChromeTarget,
  CmsSiteChromeTargetTrigger,
  cmsSiteChromeTargetCanNavigate,
  getCmsSiteChromeTargetHref,
} from '@/cms/components/chrome/cms-site-chrome-target';
import {
  type CmsSiteHeaderActionMode,
  CmsSiteHeaderActions,
} from '@/cms/components/chrome/cms-site-header-actions';
import { AppIconByName } from '@/components/icons/app-icon-registry';
import { ChevronDown, Menu, X } from '@/components/icons/app-icons';
import { getNavMinWidthMediaQuery } from '@/components/layout/navigation/header/header-breakpoint';
import { Logo } from '@/components/layout/system/logo';
import { WEBSITE_BRAND_SUFFIX } from '@/config/website-brand';
import { cn } from '@/lib/ui/utils';
import { type MouseEvent as ReactMouseEvent, useEffect, useState } from 'react';
import styles from './cms-site-header.module.css';

export type CmsSiteHeaderIcon =
  | {
      kind: 'app';
      name: string;
    }
  | {
      kind: 'editorial';
      name: string;
    };

export type CmsSiteHeaderNavigationNode = {
  badge?: string;
  children: CmsSiteHeaderNavigationNode[];
  editPath?: readonly number[];
  icon?: CmsSiteHeaderIcon;
  label: string;
  target?: CmsSiteChromeTarget;
};

export type CmsSiteHeaderNavigationColumn = {
  editPath?: readonly number[];
  links: CmsSiteHeaderNavigationNode[];
  title?: string;
};

export type CmsSiteHeaderNavigationItem = Omit<
  CmsSiteHeaderNavigationNode,
  'children'
> & {
  columns: CmsSiteHeaderNavigationColumn[];
  dataAttributes?: CmsSiteChromeEditTargetAttributes;
};

export type CmsSiteHeaderEditTargets = {
  accountLogin?: CmsSiteChromeEditTargetAttributes;
  accountPrimaryCta?: CmsSiteChromeEditTargetAttributes;
  brand?: CmsSiteChromeEditTargetAttributes;
  navigationItem?: (
    path: readonly number[]
  ) => CmsSiteChromeEditTargetAttributes | undefined;
};

export type CmsSiteHeaderModel = {
  account: {
    actionMode: CmsSiteHeaderActionMode;
    hasWorkspaceTarget: boolean;
    primaryCtaLabel?: string;
    primaryCtaTarget?: import(
      '@/site-runtime/runtime-config-contract'
    ).SiteRuntimeTarget;
    utilityLabel?: string;
    viewerMode?: import('@/cms/chrome/shell-viewer-mode').CmsShellViewerMode;
    workspaceLaunchContext?: import(
      '@/cms/workspace/launch-context'
    ).CmsWorkspaceLaunchContext;
  };
  brand: {
    label: string;
    target?: CmsSiteChromeTarget;
  };
  currentPath: string;
  editTargets?: CmsSiteHeaderEditTargets;
  locale: string;
  navigation: CmsSiteHeaderNavigationItem[];
  uiCopy: {
    closeNavigation: string;
    mobileNavigation: string;
    openNavigation: string;
    primaryNavigation: string;
  };
};

function renderHeaderIcon(
  icon: CmsSiteHeaderIcon | undefined,
  className: string
) {
  if (!icon) {
    return null;
  }

  if (icon.kind === 'app') {
    return (
      <AppIconByName
        aria-hidden="true"
        className={className}
        name={icon.name}
      />
    );
  }

  return (
    <CmsEditorialIcon
      aria-hidden="true"
      className={className}
      name={icon.name}
    />
  );
}

function HeaderNavLabel({
  icon,
  label,
  badge,
}: {
  badge?: string;
  icon?: CmsSiteHeaderIcon;
  label: string;
}) {
  return (
    <span className={styles.navLabelWrap}>
      {icon ? renderHeaderIcon(icon, 'size-4') : null}
      <span className={styles.navLabelStack}>
        <span>{label}</span>
        {badge ? (
          <span className={styles.navBadge}>{badge.toUpperCase()}</span>
        ) : null}
      </span>
    </span>
  );
}

function HeaderDropdownLink({
  editTarget,
  link,
}: {
  editTarget?: CmsSiteChromeEditTargetAttributes;
  link: CmsSiteHeaderNavigationNode;
}) {
  return (
    <CmsSiteChromeTargetTrigger
      className={styles.dropdownLink}
      dataAttributes={editTarget}
      target={link.target}
    >
      {link.icon ? renderHeaderIcon(link.icon, styles.dropdownLinkIcon) : null}
      <span className={styles.dropdownLinkTitle}>
        <span>{link.label}</span>
        {link.badge ? (
          <CmsNavLinkBadge
            className={styles.dropdownLinkBadge}
            label={link.badge}
          />
        ) : null}
      </span>
    </CmsSiteChromeTargetTrigger>
  );
}

function HeaderDesktopNavigation({
  editTargets,
  items,
  label,
}: {
  editTargets?: CmsSiteHeaderEditTargets;
  items: CmsSiteHeaderNavigationItem[];
  label: string;
}) {
  const dropdownItems = items
    .map((item, index) => ({ index, item }))
    .filter(({ item }) => item.columns.length > 0);
  const [activeDropdownIndex, setActiveDropdownIndex] = useState<number | null>(
    null
  );
  const [dropdownOffset, setDropdownOffset] = useState<number | null>(null);
  const [dropdownDirection, setDropdownDirection] = useState<
    'left' | 'right' | 'none'
  >('none');

  function openDropdown(index: number, event: ReactMouseEvent<HTMLDivElement>) {
    if (!items[index]?.columns.length) {
      setActiveDropdownIndex(null);
      setDropdownOffset(null);
      setDropdownDirection('none');
      return;
    }

    if (activeDropdownIndex === null || activeDropdownIndex === index) {
      setDropdownDirection('none');
    } else {
      setDropdownDirection(index > activeDropdownIndex ? 'right' : 'left');
    }
    setActiveDropdownIndex(index);
    setDropdownOffset(
      event.currentTarget.offsetLeft + event.currentTarget.offsetWidth / 2
    );
  }

  return (
    <nav
      aria-label={label}
      className={styles.desktopNav}
      onMouseLeave={() => {
        setActiveDropdownIndex(null);
        setDropdownOffset(null);
        setDropdownDirection('none');
      }}
    >
      {items.map((item, index) => {
        const hasChildren = item.columns.length > 0;
        const editTarget = editTargets?.navigationItem?.(
          item.editPath ?? [index]
        );
        const navContent = (
          <HeaderNavLabel
            badge={item.badge}
            icon={item.icon}
            label={item.label}
          />
        );

        return (
          <div
            className={styles.navItem}
            key={`${item.label}:${index}`}
            onMouseEnter={(event) => openDropdown(index, event)}
            {...item.dataAttributes}
          >
            {hasChildren ? (
              cmsSiteChromeTargetCanNavigate(item.target) ? (
                <CmsSiteChromeTargetTrigger
                  className={`${styles.navLink} ${styles.navTrigger} ${
                    activeDropdownIndex === index ? styles.navLinkActive : ''
                  }`}
                  dataAttributes={editTarget}
                  target={item.target}
                >
                  {navContent}
                  <ChevronDown aria-hidden="true" className="size-4" />
                </CmsSiteChromeTargetTrigger>
              ) : (
                <button
                  aria-expanded={activeDropdownIndex === index}
                  aria-haspopup="menu"
                  className={`${styles.navLink} ${styles.navTrigger} ${
                    activeDropdownIndex === index ? styles.navLinkActive : ''
                  }`}
                  {...editTarget}
                  type="button"
                >
                  {navContent}
                  <ChevronDown aria-hidden="true" className="size-4" />
                </button>
              )
            ) : (
              <CmsSiteChromeTargetTrigger
                className={styles.navLink}
                dataAttributes={editTarget}
                target={item.target}
              >
                {navContent}
              </CmsSiteChromeTargetTrigger>
            )}
          </div>
        );
      })}

      {dropdownItems.length > 0 ? (
        <div
          className={`${styles.dropdownWrap} ${
            activeDropdownIndex !== null ? styles.dropdownWrapOpen : ''
          }`}
          role="menu"
          style={
            dropdownOffset !== null
              ? { left: `${dropdownOffset}px` }
              : undefined
          }
        >
          <div className={styles.dropdown}>
            {dropdownItems.map(({ index, item }) => (
              <div
                aria-hidden={activeDropdownIndex !== index}
                className={`${styles.dropdownContent} ${
                  activeDropdownIndex === index
                    ? styles.dropdownContentActive
                    : ''
                } ${
                  activeDropdownIndex === index && dropdownDirection === 'right'
                    ? styles.dropdownSlideFromRight
                    : ''
                } ${
                  activeDropdownIndex === index && dropdownDirection === 'left'
                    ? styles.dropdownSlideFromLeft
                    : ''
                }`}
                key={`${item.label}:${index}`}
              >
                <div className={styles.dropdownGrid}>
                  {item.columns.map((column, columnIndex) => (
                    <div key={`${item.label}:${column.title ?? columnIndex}`}>
                      {column.title ? (
                        <p className={styles.dropdownColumnTitle}>
                          {column.title}
                        </p>
                      ) : null}
                      <div className={styles.dropdownLinks}>
                        {column.links.map((link, linkIndex) => (
                          <HeaderDropdownLink
                            editTarget={editTargets?.navigationItem?.(
                              link.editPath ?? [index, columnIndex, linkIndex]
                            )}
                            key={`${link.label}:${linkIndex}`}
                            link={link}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </nav>
  );
}

function getMobileTargetPath(target: CmsSiteChromeTarget | undefined) {
  return getCmsSiteChromeTargetHref(target);
}

function isMobilePathActive(
  target: CmsSiteChromeTarget | undefined,
  currentPath: string
) {
  const href = getMobileTargetPath(target);
  if (!href) {
    return false;
  }

  if (href === '/') {
    return currentPath === '/' || currentPath.endsWith('/') === true;
  }

  return currentPath === href || currentPath.endsWith(href);
}

function MobileNavigationNode({
  currentPath,
  depth,
  editTargets,
  node,
  onNavigate,
  path,
}: {
  currentPath: string;
  depth: number;
  editTargets?: CmsSiteHeaderEditTargets;
  node: CmsSiteHeaderNavigationNode;
  onNavigate: () => void;
  path: readonly number[];
}) {
  const [expanded, setExpanded] = useState(false);
  const active = isMobilePathActive(node.target, currentPath);
  const editTarget = editTargets?.navigationItem?.(node.editPath ?? path);

  if (node.children.length > 0) {
    const content = (
      <span className="flex min-w-0 items-center gap-2">
        {node.icon ? renderHeaderIcon(node.icon, 'size-6 shrink-0') : null}
        <span className="min-w-0 truncate">{node.label}</span>
        {node.badge ? <CmsNavLinkBadge label={node.badge} /> : null}
      </span>
    );

    if (depth > 0) {
      return (
        <div className="mt-3">
          <div className="py-2 text-on-surface-variant text-sm" {...editTarget}>
            {content}
          </div>
          <div className="pl-5">
            {node.children.map((child, index) => (
              <MobileNavigationNode
                currentPath={currentPath}
                depth={depth + 1}
                editTargets={editTargets}
                key={`${child.label}:${index}`}
                node={child}
                onNavigate={onNavigate}
                path={[...path, index]}
              />
            ))}
          </div>
        </div>
      );
    }

    const childrenId = `cms-mobile-navigation-${path.join('-')}`;
    const disclosureIcon = (
      <ChevronDown
        aria-hidden="true"
        className={`${styles.mobileNavigationDisclosureIcon} size-5`}
      />
    );

    return (
      <div className="border-border/40 border-b">
        {cmsSiteChromeTargetCanNavigate(node.target) ? (
          <div
            className={cn(
              styles.mobileNavigationBranchRow,
              styles.mobileNavigationPrimaryItem
            )}
          >
            <CmsSiteChromeTargetTrigger
              className="min-w-0 flex-1 text-left"
              dataAttributes={editTarget}
              onClick={onNavigate}
              target={node.target}
            >
              {content}
            </CmsSiteChromeTargetTrigger>
            <button
              aria-controls={childrenId}
              aria-expanded={expanded}
              className={styles.mobileNavigationDisclosure}
              onClick={() => setExpanded((current) => !current)}
              type="button"
            >
              <span className="sr-only">{node.label}</span>
              {disclosureIcon}
            </button>
          </div>
        ) : (
          <button
            aria-controls={childrenId}
            aria-expanded={expanded}
            className={cn(
              styles.mobileNavigationBranchButton,
              styles.mobileNavigationPrimaryItem
            )}
            onClick={() => setExpanded((current) => !current)}
            type="button"
            {...editTarget}
          >
            {content}
            {disclosureIcon}
          </button>
        )}
        <div className="pb-3 pl-5" hidden={!expanded} id={childrenId}>
          {node.children.map((child, index) => (
            <MobileNavigationNode
              currentPath={currentPath}
              depth={depth + 1}
              editTargets={editTargets}
              key={`${child.label}:${index}`}
              node={child}
              onNavigate={onNavigate}
              path={[...path, index]}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <CmsSiteChromeTargetTrigger
      className={cn(
        'flex items-center gap-2 border-b border-border/40 py-3 text-sm transition-colors',
        depth === 0
          ? styles.mobileNavigationPrimaryItem
          : active
            ? 'text-on-surface'
            : 'text-on-surface-variant hover:text-on-surface'
      )}
      dataAttributes={editTarget}
      onClick={onNavigate}
      target={node.target}
    >
      {node.icon ? renderHeaderIcon(node.icon, 'size-5 shrink-0') : null}
      <span className="min-w-0 truncate">{node.label}</span>
      {node.badge ? <CmsNavLinkBadge label={node.badge} /> : null}
    </CmsSiteChromeTargetTrigger>
  );
}

function MobileNavigation({
  currentPath,
  editTargets,
  items,
  label,
  onNavigate,
}: {
  currentPath: string;
  editTargets?: CmsSiteHeaderEditTargets;
  items: CmsSiteHeaderNavigationItem[];
  label: string;
  onNavigate: () => void;
}) {
  return (
    <nav aria-label={label}>
      {items
        .filter((item) => item.columns.length > 0 || item.target)
        .map((item, index) => {
          const { columns, ...node } = item;
          const children = columns.flatMap((column, columnIndex) => {
            if (!column.title) {
              return column.links;
            }

            return [
              {
                children: column.links,
                editPath: column.editPath ?? [index, columnIndex],
                label: column.title,
              },
            ];
          });

          return (
            <MobileNavigationNode
              currentPath={currentPath}
              depth={0}
              editTargets={editTargets}
              key={`${item.label}:${index}`}
              node={{ ...node, children }}
              onNavigate={onNavigate}
              path={[index]}
            />
          );
        })}
    </nav>
  );
}

export function CmsSiteHeaderView({ model }: { model: CmsSiteHeaderModel }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [model.currentPath]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(getNavMinWidthMediaQuery());
    const handleDesktopChange = () => {
      if (mediaQueryList.matches) {
        setMobileOpen(false);
      }
    };

    handleDesktopChange();
    mediaQueryList.addEventListener('change', handleDesktopChange);
    return () =>
      mediaQueryList.removeEventListener('change', handleDesktopChange);
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = mobileOpen ? 'hidden' : previousOverflow;
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  return (
    <CmsSiteHeaderActions
      accountLoginDataAttributes={model.editTargets?.accountLogin}
      accountPrimaryCtaDataAttributes={model.editTargets?.accountPrimaryCta}
      actionMode={model.account.actionMode}
      hasWorkspaceTarget={model.account.hasWorkspaceTarget}
      locale={model.locale}
      primaryCtaLabel={model.account.primaryCtaLabel}
      primaryCtaTarget={model.account.primaryCtaTarget}
      utilityLabel={model.account.utilityLabel}
      viewerMode={model.account.viewerMode}
      workspaceLaunchContext={model.account.workspaceLaunchContext}
    >
      {(accountContent) => (
        <header
          className={`cms-width-guard ${styles.header}`}
          data-site-runtime-chrome="header"
        >
          <div className={`cms-section cms-width-guard ${styles.bar}`}>
            <div className={styles.brandWrap}>
              <button
                aria-expanded={mobileOpen}
                aria-label={
                  mobileOpen
                    ? model.uiCopy.closeNavigation
                    : model.uiCopy.openNavigation
                }
                className={styles.mobileNavigationTrigger}
                onClick={() => setMobileOpen((current) => !current)}
                type="button"
              >
                {mobileOpen ? (
                  <X aria-hidden="true" className="size-7 stroke-[2.2]" />
                ) : (
                  <Menu aria-hidden="true" className="size-7 stroke-[2.2]" />
                )}
              </button>

              <CmsSiteChromeTargetTrigger
                className={styles.brand}
                dataAttributes={model.editTargets?.brand}
                target={model.brand.target}
              >
                <Logo
                  className={`${styles.brandLogo} ${styles.mobileHeaderLogo}`}
                />
                <span className={styles.brandTextWrap}>
                  <span className={styles.brandText}>{model.brand.label}</span>
                  <span className={styles.brandSuffix}>
                    {WEBSITE_BRAND_SUFFIX}
                  </span>
                </span>
              </CmsSiteChromeTargetTrigger>
            </div>

            <HeaderDesktopNavigation
              editTargets={model.editTargets}
              items={model.navigation}
              label={model.uiCopy.primaryNavigation}
            />

            <div className={`cms-width-guard ${styles.actions}`}>
              {accountContent}
            </div>
          </div>

          <div
            aria-hidden={!mobileOpen}
            aria-label={model.uiCopy.mobileNavigation}
            className={`fixed inset-x-0 bottom-0 z-[55] overflow-x-clip overflow-y-auto border-border/40 border-t bg-background px-6 pt-4 pb-10 text-on-surface transition-all duration-300 ease-out lg:hidden ${
              mobileOpen
                ? 'visible opacity-100'
                : 'invisible pointer-events-none opacity-0'
            }`}
            style={{
              top: 'calc(var(--cms-site-banner-offset, 0px) + var(--cms-mobile-header-height))',
            }}
          >
            <MobileNavigation
              currentPath={model.currentPath}
              editTargets={model.editTargets}
              items={model.navigation}
              label={model.uiCopy.mobileNavigation}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </header>
      )}
    </CmsSiteHeaderActions>
  );
}
